import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { MenuItem } from '../../types';
import { resolveLocalized } from '../../utils/localization';

export default function Header() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cmsMenuItems, setCmsMenuItems] = useState<MenuItem[] | null>(null);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [siteName, setSiteName] = useState('IABFM');

  const defaultMenuItems: MenuItem[] = useMemo(() => [
    { label: t('header.courses'), type: 'link', target: '/courses', order: 0, visible: true },
    { label: t('header.exams'), type: 'link', target: '/exams', order: 1, visible: true },
    { label: t('header.institute'), type: 'scroll', target: 'institute', order: 2, visible: true },
    { label: t('header.certifiedTrainers'), type: 'scroll', target: 'trainers', order: 3, visible: true },
    { label: t('header.partners'), type: 'link', target: '/partners', order: 4, visible: true },
    { label: t('header.testimonials'), type: 'scroll', target: 'testimonials', order: 5, visible: true },
    { label: t('nav.contactUs'), type: 'scroll', target: 'contact', order: 6, visible: true },
  ], [t]);

  const menuItems = cmsMenuItems || defaultMenuItems;

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await api.get('/site-content');
        const data = response.data;
        if (data?.headerMenu && Array.isArray(data.headerMenu)) {
          setCmsMenuItems(data.headerMenu.filter((item: MenuItem) => item.visible).sort((a: MenuItem, b: MenuItem) => a.order - b.order));
        }
        if (data?.branding) {
          if (data.branding.logoUrl) setLogoUrl(data.branding.logoUrl);
          if (data.branding.siteName) setSiteName(data.branding.siteName);
          if (data.branding.faviconUrl) {
            const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
            if (link) link.href = data.branding.faviconUrl;
          }
        }
      } catch {
        // fall back to translated defaults
      }
    };
    fetchMenu();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const location = useLocation();

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
      isScrolled ? 'glass-refined shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between transition-all duration-200 ${isScrolled ? 'h-16' : 'h-20'}`}>
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoUrl} alt={siteName} className={`logo-dark transition-all duration-200 ${isScrolled ? 'h-16 -my-4' : 'h-24 -my-7'}`} />
              <span className="font-display text-2xl font-bold text-gray-900">{siteName}</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              item.type === 'link' ? (
                <Link key={resolveLocalized(item.label as any, i18n.language)} to={item.target} className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-300">
                  {resolveLocalized(item.label as any, i18n.language)}
                </Link>
              ) : (
                <button key={resolveLocalized(item.label as any, i18n.language)} onClick={() => scrollToSection(item.target)} className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-300">
                  {resolveLocalized(item.label as any, i18n.language)}
                </button>
              )
            ))}
          </nav>

          {/* Desktop Auth / CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-300 border border-gray-200"
                >
                  <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-gray-700">{user?.name}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 glass-elevated rounded-xl py-2 overflow-hidden">
                    <Link
                      to="/dashboard"
                      className="flex items-center px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-5 h-5 mr-3" />
                      {t('nav.dashboard')}
                    </Link>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-5 h-5 mr-3" />
                        {t('nav.adminPanel')}
                      </Link>
                    )}
                    <hr className="my-2 border-gray-200" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-3 text-gray-600 hover:text-red-600 hover:bg-gray-100 transition-colors"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors px-4 py-2">
                  {t('nav.login')}
                </Link>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="btn-gold px-5 py-2.5 shadow-gold-sm"
                >
                  {t('nav.contactUs')}
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - slide down animation */}
      <div
        className={`lg:hidden glass-refined border-t border-gray-200 shadow-lg overflow-hidden transition-all duration-500 ease-out ${
          isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-3 space-y-2">
          {menuItems.map((item) => (
            item.type === 'link' ? (
              <Link key={resolveLocalized(item.label as any, i18n.language)} to={item.target} onClick={() => setIsMenuOpen(false)} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors">
                {resolveLocalized(item.label as any, i18n.language)}
              </Link>
            ) : (
              <button key={resolveLocalized(item.label as any, i18n.language)} onClick={() => scrollToSection(item.target)} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors">
                {resolveLocalized(item.label as any, i18n.language)}
              </button>
            )
          ))}

          <hr className="border-gray-200 my-2" />

          {isAuthenticated ? (
            <>
              <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                {t('nav.dashboard')}
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                  {t('nav.adminPanel')}
                </Link>
              )}
              <button onClick={handleLogout} className="block w-full text-left py-3 px-4 text-red-600 hover:bg-gray-100 rounded-lg transition-colors">
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                {t('nav.login')}
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full py-3 px-4 btn-gold text-center mt-3 rounded-xl"
              >
                {t('nav.contactUs')}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
